import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'antd';
import { QuestionCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { WidgetState, HubServiceState, WidgetType } from '@kemu-io/kemu-core/types';
import { Tooltip } from 'antd/lib';
import { useSelector } from 'react-redux';
import styles from './WidgetHelpBar.module.css';
import useTranslation from '@hooks/useTranslation';
import useHubServiceInfo from '@hooks/useHubServiceInfo';
import useMarketplaceNavigator from '@hooks/useMarketplaceNavigator';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import { ABORT_DRAGGING_CLASS, DefaultPlatformArch } from '@common/constants';
import { selectFolderPath } from '@src/features/Workspace/workspaceSlice';

type WidgetHelpBarProps = {
  className?: string;
  widgetId: string;
  widgetType: WidgetType;
  parentGroupId?: string;
  widgetState: WidgetState;
  onShowHelp: () => void;
};

/**
 * Renders a help bar with a help button that appears on hover
 * @param onShowHelp - Callback function to show help content
 */
const WidgetHelpBar = ({
  onShowHelp,
  className,
  widgetId,
  widgetType,
  widgetState,
  parentGroupId,
}: WidgetHelpBarProps) => {
  const cW = useTranslation('CanvasWidget');
  const getServiceInfo = useHubServiceInfo();
  const { systemInfo } = useLogicMapperContext();
  const [isMissingHubService, setIsMissingHubService] = useState<boolean>(false);
  const { navigateToService } = useMarketplaceNavigator();
  const folderPath = useSelector(selectFolderPath);

  const insideGroup = (folderPath?.length || 0) > 0;
  const hasHelp = widgetType === WidgetType.hubService && (widgetState as HubServiceState).service?.mdHelp;
	const isNativeWidget = widgetType !== WidgetType.hubService;
  const isVisible = folderPath?.some(path => path.groupId === parentGroupId)
    || (!folderPath?.length && !parentGroupId);

  const handleDownloadService = () => {
    console.log('download service', widgetId);
    const hsState = widgetState as HubServiceState;
    if (hsState.service) {
      navigateToService({
        name: hsState.service.name,
        version: hsState.service.version,
        // Select the platform arch for the current system
        platform: systemInfo?.platformArch || DefaultPlatformArch
      });
    }
  };

  useEffect(() => {
    if (widgetType === WidgetType.hubService) {
      const hsState = widgetState as HubServiceState;
      if (hsState?.service) {
        const serviceInfo = getServiceInfo(hsState.service.name, hsState.service.version);
        if (serviceInfo) {
          setIsMissingHubService(false);
        } else {
          setIsMissingHubService(true);
        }
      }
    }
  }, [widgetState, getServiceInfo, widgetType]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={classNames(styles.HelpBar, className, ABORT_DRAGGING_CLASS)}>
      <div className={classNames(styles.ButtonsContainer, {
        [styles.MissingService]: isMissingHubService
      })}>
        {((widgetType === WidgetType.hubService && hasHelp) || isNativeWidget) && (
          <Button
            title={cW('ShowHelp')}
            type="text"
            shape="circle"
            icon={<QuestionCircleOutlined />}
            onClick={onShowHelp}
          />
        )}
        {/* {isMissingHubService && (

        )} */}
        {isMissingHubService && (
          <Tooltip title={cW('MissingHubService.Description')}>
            <Button
              type="text"
              shape="circle"
              className={classNames(styles.DownloadServiceBtn, {
                [styles.InsideGroup]: insideGroup,
              })}
              icon={<DownloadOutlined />}
              onClick={handleDownloadService}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default WidgetHelpBar;
